/**
 * Persistent, IndexedDB-backed cache for QuikQ station responses.
 *
 * Freshness is anchored to **9:00 AM and 5:00 PM America/Chicago** — a cache
 * entry is considered fresh if it was written after the most recent boundary,
 * so the dataset is effectively refreshed twice per day at fixed wall-clock
 * times rather than after a rolling TTL.
 *
 * The cache implements a stale-while-revalidate read pattern: stale entries
 * are still returned to the caller (so the map can render instantly), but
 * the caller is expected to kick off a background re-fetch to update them.
 *
 * Maintainer note: keep caching logic here and in `quikqApi.ts` / `quikqAuth.ts`.
 * Map UI/overlay/marker changes belong in `MapRoutesMapLibre.tsx` — submit those
 * in separate PRs from cache or auth changes when possible.
 */
import type { QuikQStation } from './quikqApi';

const DB_NAME = 'nsf_stations_cache_v1';
const STORE_NAME = 'entries';
const ROUTE_STORE_NAME = 'routeEntries';
const DB_VERSION = 2;
const MAX_ENTRY_AGE_MS = 14 * 24 * 60 * 60 * 1000; // hard cap, evict after 14 days

export interface StationsCacheEntry {
  key: string;
  ts: number;
  data: QuikQStation[];
  startCode: string;
}

export interface StationsCacheReadResult {
  data: QuikQStation[];
  isFresh: boolean;
  cachedAt: number;
}

const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!isBrowser) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('ts', 'ts', { unique: false });
        }
        if (!db.objectStoreNames.contains(ROUTE_STORE_NAME)) {
          const routeStore = db.createObjectStore(ROUTE_STORE_NAME, { keyPath: 'key' });
          routeStore.createIndex('ts', 'ts', { unique: false });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.warn('stationsCache: failed to open IndexedDB', request.error);
        resolve(null);
      };
      request.onblocked = () => {
        console.warn('stationsCache: IndexedDB open blocked');
        resolve(null);
      };
    } catch (error) {
      console.warn('stationsCache: IndexedDB unavailable', error);
      resolve(null);
    }
  });

  return dbPromise;
}

function txStore(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Compute milliseconds elapsed since the most recent 9 AM / 5 PM Chicago
 * boundary. A cache entry is fresh iff its age (now - ts) is less than this
 * value, meaning it was written after the most recent boundary.
 */
function msSinceMostRecentChicagoBoundary(now: Date = new Date()): number {
  // Pull hour/minute/second components of the current time as displayed in
  // America/Chicago. Intl.DateTimeFormat handles DST and offset rules for us.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  let h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  if (h === 24) h = 0; // some locales report 24:00 instead of 00:00
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const s = parseInt(parts.find((p) => p.type === 'second')?.value ?? '0', 10);

  const secondsSinceChicagoMidnight = h * 3600 + m * 60 + s;
  const BOUNDARY_9AM = 9 * 3600;
  const BOUNDARY_5PM = 17 * 3600;

  let secondsSinceBoundary: number;
  if (secondsSinceChicagoMidnight >= BOUNDARY_5PM) {
    secondsSinceBoundary = secondsSinceChicagoMidnight - BOUNDARY_5PM;
  } else if (secondsSinceChicagoMidnight >= BOUNDARY_9AM) {
    secondsSinceBoundary = secondsSinceChicagoMidnight - BOUNDARY_9AM;
  } else {
    // Before 9 AM Chicago — the most recent boundary was yesterday's 5 PM.
    // 7 hours from yesterday's 5 PM to today's midnight + seconds today.
    secondsSinceBoundary = (24 * 3600 - BOUNDARY_5PM) + secondsSinceChicagoMidnight;
  }

  return secondsSinceBoundary * 1000;
}

/**
 * True if a cache entry written at `ts` (UTC ms) is still considered fresh
 * against the Chicago 9 AM / 5 PM refresh schedule.
 */
export function isFreshAgainstChicagoSchedule(ts: number, now: number = Date.now()): boolean {
  const msSinceBoundary = msSinceMostRecentChicagoBoundary(new Date(now));
  const age = now - ts;
  return age >= 0 && age < msSinceBoundary;
}

export async function getCachedStations(key: string): Promise<StationsCacheReadResult | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const entry = (await reqAsPromise(txStore(db, 'readonly').get(key))) as StationsCacheEntry | undefined;
    if (!entry || !Array.isArray(entry.data)) return null;

    // Discard entries older than the hard cap regardless of schedule.
    if (Date.now() - entry.ts > MAX_ENTRY_AGE_MS) {
      // Best-effort delete; ignore errors.
      try {
        await reqAsPromise(txStore(db, 'readwrite').delete(key));
      } catch {
        // ignore
      }
      return null;
    }

    return {
      data: entry.data,
      isFresh: isFreshAgainstChicagoSchedule(entry.ts),
      cachedAt: entry.ts,
    };
  } catch (error) {
    console.warn('stationsCache: read failed', error);
    return null;
  }
}

export async function setCachedStations(
  key: string,
  data: QuikQStation[],
  startCode: string,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const entry: StationsCacheEntry = {
      key,
      ts: Date.now(),
      data,
      startCode,
    };
    await reqAsPromise(txStore(db, 'readwrite').put(entry));
  } catch (error) {
    console.warn('stationsCache: write failed', error);
  }
}

export async function clearStationsCache(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    await reqAsPromise(txStore(db, 'readwrite').clear());
    await reqAsPromise(
      db.transaction(ROUTE_STORE_NAME, 'readwrite').objectStore(ROUTE_STORE_NAME).clear(),
    );
  } catch (error) {
    console.warn('stationsCache: clear failed', error);
  }
}

/** Remove entries written before the most recent Chicago refresh boundary. */
export async function pruneStaleCacheEntries(): Promise<number> {
  const db = await openDb();
  if (!db) return 0;
  const now = Date.now();
  let removed = 0;
  for (const storeName of [STORE_NAME, ROUTE_STORE_NAME]) {
    try {
      const readStore = db.transaction(storeName, 'readonly').objectStore(storeName);
      const allKeys = (await reqAsPromise(readStore.getAllKeys())) as IDBValidKey[];
      const allEntries = (await reqAsPromise(readStore.getAll())) as StationsCacheEntry[];
      for (let i = 0; i < allEntries.length; i++) {
        const entry = allEntries[i];
        if (!isFreshAgainstChicagoSchedule(entry.ts, now)) {
          try {
            const writeStore = db.transaction(storeName, 'readwrite').objectStore(storeName);
            await reqAsPromise(writeStore.delete(allKeys[i]));
            removed++;
          } catch {
            // ignore
          }
        }
      }
    } catch (error) {
      console.warn(`stationsCache: prune failed for ${storeName}`, error);
    }
  }
  return removed;
}

// ---------------------------------------------------------------------------
// Route-level cache: keyed by a hash of the route (start + end + checkpoints).
// Lets the second creation of the *same* route load all of its stations
// instantly without re-fetching every sample point along the polyline.
// ---------------------------------------------------------------------------

export interface RouteStationsCacheEntry {
  key: string;
  ts: number;
  data: QuikQStation[];
  startCode: string;
}

function txRouteStore(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(ROUTE_STORE_NAME, mode).objectStore(ROUTE_STORE_NAME);
}

export function buildRouteCacheKey(input: {
  startCode: string;
  start: { lat: number; lng: number };
  end: { lat: number; lng: number };
  checkpoints?: Array<{ lat: number; lng: number }>;
}): string {
  // Round to ~10m precision so trivial GPS drift doesn't bust the cache.
  const fmt = (n: number) => n.toFixed(4);
  const checkpointStr = (input.checkpoints ?? [])
    .map((c) => `${fmt(c.lat)},${fmt(c.lng)}`)
    .join('>');
  return [
    input.startCode,
    `${fmt(input.start.lat)},${fmt(input.start.lng)}`,
    checkpointStr,
    `${fmt(input.end.lat)},${fmt(input.end.lng)}`,
  ].join('|');
}

export async function getCachedRouteStations(
  key: string,
): Promise<StationsCacheReadResult | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const entry = (await reqAsPromise(txRouteStore(db, 'readonly').get(key))) as
      | RouteStationsCacheEntry
      | undefined;
    if (!entry || !Array.isArray(entry.data)) return null;
    if (Date.now() - entry.ts > MAX_ENTRY_AGE_MS) {
      try {
        await reqAsPromise(txRouteStore(db, 'readwrite').delete(key));
      } catch {
        // ignore
      }
      return null;
    }
    return {
      data: entry.data,
      isFresh: isFreshAgainstChicagoSchedule(entry.ts),
      cachedAt: entry.ts,
    };
  } catch (error) {
    console.warn('stationsCache(route): read failed', error);
    return null;
  }
}

export async function setCachedRouteStations(
  key: string,
  data: QuikQStation[],
  startCode: string,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const entry: RouteStationsCacheEntry = {
      key,
      ts: Date.now(),
      data,
      startCode,
    };
    await reqAsPromise(txRouteStore(db, 'readwrite').put(entry));
  } catch (error) {
    console.warn('stationsCache(route): write failed', error);
  }
}

/**
 * Returns the next scheduled refresh time as a Chicago-formatted string
 * (useful for diagnostics, e.g. "next refresh at 5:00 PM CT").
 */
export function describeNextRefreshBoundary(now: Date = new Date()): string {
  const msSinceBoundary = msSinceMostRecentChicagoBoundary(now);
  // The next boundary is the previous-boundary + 8h (9am → 5pm) or 16h (5pm → 9am next day).
  // Simpler: compute current chicago hour to decide.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  let h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  if (h === 24) h = 0;
  if (h >= 17) return 'next refresh: 9:00 AM CT (tomorrow)';
  if (h >= 9) return 'next refresh: 5:00 PM CT (today)';
  return `next refresh: 9:00 AM CT (today), ${Math.round(msSinceBoundary / 60000)} min since last boundary`;
}
